<template>
  <LoaderWrapper ref="modalLoader" class="h-100">
    <div class="row mt-2" v-if="isAdmin || isOperator">
      <div class="col-sm-12">
        <small class="text-secondary">{{helper.trans('labels.comment')}}: {{trader?.comment ?? 'n/a'}}</small>
      </div>
    </div>

    <div class="row mt-2" v-if="isAdmin || isOperator">
      <div class="col-sm-12 d-flex justify-content-between">
        <div class="d-flex align-items-center gap-2">
          <label for="toggleVisibility" class="text-capitalize-first">{{ helper.trans("labels.only_active") }}</label>
          <div class="form-check form-switch d-flex align-items-center justify-content-center">
            <input id="toggleVisibility" v-model="onlyActive" class="form-check-input" type="checkbox" role="switch" style="cursor: pointer;">
          </div>
        </div>

        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click.prevent="showHistory">
          {{ helper.trans('labels.history') }}
        </button>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-sm-12">
        <input type="text" class="form-control" v-model="searchQuery" @input="searchGroups" :placeholder="helper.trans('labels.search_merchant_or_group')" autocomplete="off">
      </div>
    </div>

    <div class="row mb-2 mt-2">
      <div class="col-sm-12 overflow-auto">
        <div v-if="groupsLoaded && visibleGroupIds.length === 0" class="text-center text-muted py-3">
          {{ helper.trans('labels.no_merchant_groups_found') }}
        </div>
        <table v-else class="table table-bordered">
          <thead>
          <tr class="bg-light">
            <th rowspan="2" class="text-capitalize-first">{{ helper.trans('labels.traffic_group') }}</th>
            <th rowspan="2" class="text-capitalize-first">{{ helper.trans('labels.type') }}</th>
            <th rowspan="2" class="text-capitalize-first" style="width: 0px"></th>
            <th v-if="!isTeamLead" rowspan="2" class="text-capitalize-first">{{ helper.trans('labels.priority') }}</th>
            <th colspan="6" class="text-center">{{ helper.trans('labels.reward_fee_percent')}}</th>
            <th v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" rowspan="2" class="text-capitalize">
              {{ helper.trans('labels.teamlead') }}
              <small class="text-secondary">{{ trader?.mainTeamLead?.name ?? 'n/a' }}</small>
            </th>
            <th v-if="!isTeamLead" rowspan="2" colspan="2" class="text-capitalize-first text-center">{{ helper.trans('labels.min_max_store_reward')}}</th>
            <th rowspan="2" v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" class="text-capitalize">{{ helper.trans('labels.total') }}</th>
            <th rowspan="2" v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" class="text-capitalize">{{ helper.trans('labels.ranges') }}</th>
            <th rowspan="2" v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" class="text-capitalize-first">{{ helper.trans('labels.store_blacklist') }}</th>
          </tr>
          <tr>
            <th class="text-capitalize">{{ helper.trans('labels.trader') }}</th>

            <template v-for="(teamLead, index) in trader?.teamLeads" :key="index">
              <th v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id || teamLead?.id === user?.id))" class="text-capitalize">
                {{ helper.trans('labels.teamlead') }}
                <small class="text-secondary">{{ teamLead?.name ?? 'n/a' }}</small>
              </th>
            </template>
          </tr>
          </thead>
          <tbody>
          <template v-for="item in sortedSettings" :key="item.trafficGroupId">
            <tr :class="{'bg-warning': !isValidRewards(item)}" v-if="onlyActive && item.enabled || !onlyActive">
              <td class="bg-white align-middle">{{item._trafficGroup?.name}} <i v-if="item._trafficGroup?.isHidden" class="fa fa-eye-slash"></i></td>
              <td class="bg-white align-middle text-center">
                <i v-if="item._trafficGroup?.type === Direction.PAY_IN" v-b-tooltip.hover :title="helper.trans(`direction.${item._trafficGroup?.type}`)" class="fa-regular fa-circle-down text-primary"></i>
                <i v-else v-b-tooltip.hover :title="helper.trans(`direction.${item._trafficGroup?.type}`)" class="fa-regular fa-circle-up text-warning"></i>
              </td>
              <td class="bg-white">
                <div class="form-check form-switch d-flex align-items-center justify-content-center">
                  <input v-model="item.enabled" :disabled="isTeamLead" id="autoclose" class="form-check-input" style="cursor: pointer" type="checkbox" role="switch"  >
                </div>
              </td>
              <td v-if="!isTeamLead" class="bg-white">
                <button  type="button" class="btn btn-sm w-100" :class="priorityClass(item.priority)" @click.prevent="togglePriority(item)">{{ item.priority }}</button>
              </td>
              <td style="width: 100px"><input class="form-control form-control-sm" :readonly="isTeamLead && trader?.mainTeamLead?.id !== user?.id" v-model="item._rewardFeePercent"></td>
              <template v-for="(teamLead, index) in trader?.teamLeads" :key="index">
                <td v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id || teamLead?.id === user?.id))" style="width: 100px">
                  <input class="form-control form-control-sm" :readonly="isTeamLead && trader?.mainTeamLead?.id !== user?.id || !teamLead" v-model="item._teamLeadRewardFeePercents[index]">
                </td>
              </template>
              <td v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" style="width: 100px"><input class="form-control form-control-sm" readonly :value="item._teamLeadRewardFeePercent"></td>
              <td v-if="!isTeamLead" style="width: 100px"><input class="form-control form-control-sm" readonly :value="item._groupMinRewardPercent"></td>
              <td v-if="!isTeamLead" style="width: 100px"><input class="form-control form-control-sm" readonly :value="item._groupMaxRewardPercent"></td>
              <td v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" style="width: 100px"><input class="form-control form-control-sm" readonly :value="getTotalRewardValue(item)"></td>
              <td v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" style="width: 100px" class="text-center">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="toggleRanges(item.trafficGroupId)"
                  :title="helper.trans('labels.toggle_ranges')"
                >
                  <i class="fa" :class="expandedRanges[item.trafficGroupId] ? 'fa-eye-slash' : 'fa-eye'"></i>
                </button>
              </td>
              <td v-if="isAdmin || isOperator || (isTeamLead && (trader?.mainTeamLead?.id === user?.id))" style="width: 100px" class="text-center">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="toggleStoreBlacklist(item.trafficGroupId)"
                  :title="helper.trans('labels.store_blacklist')"
                >
                  <i class="fa" :class="expandedStoreBlacklist[item.trafficGroupId] ? 'fa-eye-slash' : 'fa-eye'"></i>
                  <span v-if="(item._excludedStores?.length ?? 0) > 0" class="badge bg-secondary ms-1">{{ item._excludedStores?.length }}</span>
                </button>
              </td>
            </tr>

            <tr v-if="expandedStoreBlacklist[item.trafficGroupId]" class="bg-light">
              <td colspan="100%" class="p-0">
                <div class="p-3">
                  <h6 class="mb-2">{{ helper.trans('labels.store_blacklist') }} - {{ item._trafficGroup?.name }}</h6>
                  <div style="max-width: 500px">
                    <StoreMultiselect
                      :multiple="true"
                      small
                      v-model="item._excludedStores"
                    />
                  </div>
                </div>
              </td>
            </tr>

            <tr v-if="expandedRanges[item.trafficGroupId]" class="bg-light">
              <td colspan="100%" class="p-0">
                <div class="p-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">{{ helper.trans('labels.fee_ranges') }} - {{ item._trafficGroup?.name }}</h6>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      @click="addNewRange(item.trafficGroupId)"
                    >
                      <i class="fa fa-plus"></i> {{ helper.trans('button.add_range') }}
                    </button>
                  </div>

                  <div v-if="item.traderFeeByDealAmount && item.traderFeeByDealAmount.length > 0" class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead>
                      <tr>
                        <th>{{ helper.trans('labels.min_sum') }}</th>
                        <th>{{ helper.trans('labels.max_sum') }}</th>
                        <th>{{ helper.trans('labels.fee') }}</th>
                        <th>{{ helper.trans('labels.currency') }}</th>
                        <th style="width: 100px">{{ helper.trans('labels.actions') }}</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(range, rangeIndex) in item.traderFeeByDealAmount" :key="rangeIndex" :class="{'table-danger': !isRangeValid(range)}">
                        <td>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{'is-invalid': !isRangeValid(range)}"
                            :value="range.minSum"
                            @input="updateRangeField(item.trafficGroupId, rangeIndex, 'minSum', $event.target.value)"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{'is-invalid': !isRangeValid(range)}"
                            :value="range.maxSum"
                            @input="updateRangeField(item.trafficGroupId, rangeIndex, 'maxSum', $event.target.value)"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            :value="range._feePercent"
                            @input="updateRangeField(item.trafficGroupId, rangeIndex, '_feePercent', $event.target.value)"
                          />
                        </td>
                        <td>
                          <select
                            class="form-control form-control-sm"
                            :class="{'is-invalid': !isRangeValid(range)}"
                            :value="range.currency"
                            @change="updateRangeField(item.trafficGroupId, rangeIndex, 'currency', $event.target.value)"
                          >
                            <option value="">{{ helper.trans('labels.select_currency') }}</option>
                            <option v-for="currency in availableCurrencies" :key="currency" :value="currency">
                              {{ currency }}
                            </option>
                          </select>
                        </td>
                        <td class="text-center">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            @click="removeRange(item.trafficGroupId, rangeIndex)"
                            :title="helper.trans('button.remove_range')"
                          >
                            <i class="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else class="text-muted text-center py-3">
                    <i class="fa fa-info-circle"></i> {{ helper.trans('messages.no_ranges_configured') }}
                  </div>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>

      </div>
    </div>

    <b-modal
      :title="helper.trans('labels.history')"
      dialog-class="modal-xl"
      :modelValue="historyModal"
      body-class="pt-0"
      footer-class="d-flex justify-content-end"
      @update:modelValue="historyModal = $event"
    >
      <template #default>
        <LoaderWrapper ref="historyLoader">
          <div v-if="historyResponse.items.length === 0" class="text-center text-muted py-4">
            {{ helper.trans('labels.no_history') }}
          </div>
          <div v-else class="overflow-auto">
            <table class="table table-striped table-sm align-middle">
              <thead>
                <tr>
                  <th style="width: 220px">{{ helper.trans('labels.created_at') }}</th>
                  <th style="width: 260px">{{ helper.trans('labels.initiator') }}</th>
                  <th>{{ helper.trans('labels.settings') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in historyResponse.items" :key="item.id">
                  <td class="text-nowrap">{{ dateHelper.format(item.changedAt) }}</td>
                  <td>
                    <UserNameLabel v-if="item.initiator" :user="item.initiator" />
                    <span v-else class="text-muted">#{{ item.initiatorId }}</span>
                  </td>
                  <td style="white-space: normal">
                    <div class="text-secondary mb-1">
                      {{ item.trafficGroupName }} ({{ helper.trans(`direction.${item.trafficGroupType}`) }})
                    </div>
                    <pre class="mb-0" style="max-height: 220px; overflow: auto">{{ formatHistoryValue(item.newValue) }}</pre>
                  </td>
                </tr>
              </tbody>
            </table>

            <b-pagination
              v-if="historyResponse.totalCount > historyResponse.items.length"
              v-model="historyPage"
              :total-rows="historyResponse.totalCount"
              :per-page="historyResponse.limit"
              :limit="20"
              @update:modelValue="loadHistory()"
              prev-text="Back"
              next-text="Next"
              align="start"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            ></b-pagination>
          </div>
        </LoaderWrapper>
      </template>
      <template #footer>
        <button type="button" @click.prevent="historyModal = false" class="btn btn-outline-dark btn-sm text-capitalize-first">
          {{ helper.trans('button.close') }}
        </button>
      </template>
    </b-modal>
  </LoaderWrapper>
</template>

<script lang="ts">
import {computed, defineComponent, Prop, PropType, reactive, ref, toRefs, watch} from "vue";
import {useStore} from "vuex";
import {debounce, helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Multiselect from "vue-multiselect";
import {api} from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import {Direction} from "@/models/enum/Direction";
import {Dictionary} from "@/types";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import TraderDTO from "@/models/TraderDTO";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import TraderGroupRewardSettingsDTO from "@/models/TraderGroupRewardSettingsDTO";
import {TrafficPriority} from "@/models/enum/TrafficPriority";
import TeamLeadRewardSettingsDTO from "@/models/TeamLeadRewardSettingsDTO";
import TraderFeeByDealAmountDTO from "@/models/TraderFeeByDealAmountDTO";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import TrafficGroupTraderRewardSettingsHistoryDTO from "@/models/TrafficGroupTraderRewardSettingsHistoryDTO";
import {BPagination} from "bootstrap-vue-3";
import UserNameLabel from "@/components/UserNameLabel.vue";
import {dateHelper} from "@/service/DateHelper";
import StoreMultiselect from "@/components/StoreMultiselect.vue";
import PaginationDTO from "@/models/PaginationDTO";


const components = {
  Multiselect,
  FormInputWrapper,
  LoaderWrapper,
  BPagination,
  UserNameLabel,
  StoreMultiselect
}

const notification = useNotification()

export default defineComponent({
  name: "TraderRewardSettingsForm",
  props: {
    trader: {
      type: Object as PropType<TraderDTO|null>,
      default: null
    } as Prop<TraderDTO|null>
  },
  components,
  setup(props) {

    const modalLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const historyLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const store = useStore();
    const state = reactive({
      onlyActive: true,
      searchQuery: '',
      groupsLoaded: false,
      visibleGroupIds: [] as number[],
      rewardSettingsByGroupId: {} as Dictionary<TraderGroupRewardSettingsDTO>,
      expandedRanges: {} as Dictionary<boolean>,
      expandedStoreBlacklist: {} as Dictionary<boolean>,
      historyModal: false,
      historyPage: 1,
      historyResponse: {
        limit: 5,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<TrafficGroupTraderRewardSettingsHistoryDTO>,
    });
    let traderLoadGeneration = 0
    let searchRequestId = 0
    let traderSettingsByGroupId = {} as Dictionary<TraderGroupRewardSettingsDTO>
    let teamLeadRewardSettingsByGroupId = {} as Dictionary<TeamLeadRewardSettingsDTO>

    const formatHistoryValue = (value: any) => {
      if (value === null || value === undefined) {
        return ''
      }
      try {
        return JSON.stringify(value, null, 2)
      } catch (e) {
        return String(value)
      }
    }

    const loadHistory = async () => {
      if (!props.trader) {
        return;
      }

      historyLoader.value?.show()
      try {
        state.historyResponse.offset = state.historyResponse.limit * (state.historyPage - 1)
        state.historyResponse = await api.call.traderRewardSettings.history(props.trader.id, state.historyResponse)
      } finally {
        historyLoader.value?.hide()
      }
    }

    const showHistory = async () => {
      state.historyModal = true
      state.historyPage = 1
      await loadHistory()
    }


    const save = async () => {
      if (!props.trader) {
        return;
      }

      let rewardSettings = [];
      for (const settings of Object.values(state.rewardSettingsByGroupId)) {
        const processedSettings = {
          trafficGroupId: settings.trafficGroupId,
          traderId: settings.traderId,
          priority: settings.priority,
          enabled: settings.enabled,
          reward: settings._rewardFeePercent ? helper.percentToFloat(settings._rewardFeePercent) : null,
          teamLeadRewards: settings._teamLeadRewardFeePercents.map(item => item ? helper.percentToFloat(item) : null),
          excludedStoreIds: (settings._excludedStores || []).map(s => s.id),
          traderFeeByDealAmount: settings.traderFeeByDealAmount ? settings.traderFeeByDealAmount.map(range => {
            const processedRange = {
              minSum: range.minSum && range.minSum !== '' ? Math.round((typeof range.minSum === 'string' ? parseFloat(range.minSum) : range.minSum) * 100) : null,
              maxSum: range.maxSum && range.maxSum !== '' ? Math.round((typeof range.maxSum === 'string' ? parseFloat(range.maxSum) : range.maxSum) * 100) : null,
              fee: range._feePercent ? helper.percentToFloat(range._feePercent) : '0',
              currency: range.currency || '',
              _feePercent: range.fee ? helper.percentToFloat(range.fee) : 0,
            }
            return processedRange
          }) : null
        }
        rewardSettings.push(processedSettings)
      }

      await api.call.traderRewardSettings.update(props.trader.id, {
        rewardSettings
      })
    }

    const floatToPercent = (value: string|null) => {
      let percent = value ? helper.floatToPercent(value) : null;
      if (isNaN(percent ?? 0)) {
        percent = null
      }
      return percent
    }

    const canShowGroupForTeamLead = (trafficGroupId: number) => {
      if (!store.getters.isTeamLead) {
        return true
      }
      const teamLeadSettings = teamLeadRewardSettingsByGroupId[trafficGroupId] ?? null
      return !!teamLeadSettings?.enabled
    }

    const hydrateSettings = async (group: TrafficGroupDTO, settings: TraderGroupRewardSettingsDTO) => {
      const teamLeadSettings = teamLeadRewardSettingsByGroupId[group.id] ?? null
      settings._trafficGroup = group
      settings._groupMinRewardPercent = floatToPercent(group.minReward)
      settings._groupMaxRewardPercent = floatToPercent(group.maxReward)
      settings._rewardFeePercent = floatToPercent(settings.rewardFee);
      settings._teamLeadRewardFeePercents = [
        floatToPercent(settings.teamLeadRewardFees[0]),
        floatToPercent(settings.teamLeadRewardFees[1]),
        floatToPercent(settings.teamLeadRewardFees[2]),
        floatToPercent(settings.teamLeadRewardFees[3]),
        floatToPercent(settings.teamLeadRewardFees[4]),
      ]
      if (teamLeadSettings) {
        settings._teamLeadRewardFeePercent = floatToPercent(teamLeadSettings.reward);
      }

      if (!settings.traderFeeByDealAmount) {
        settings.traderFeeByDealAmount = []
      } else {
        settings.traderFeeByDealAmount = settings.traderFeeByDealAmount.map(range => {
          const newRange = {
            minSum: range.minSum !== null && range.minSum !== undefined ? (parseFloat(range.minSum.toString()) / 100).toString() : '',
            maxSum: range.maxSum !== null && range.maxSum !== undefined ? (parseFloat(range.maxSum.toString()) / 100).toString() : '',
            fee: range.fee || '',
            currency: range.currency || '',
            _feePercent: range.fee ? floatToPercent(range.fee) : 0,
          }
          return reactive(newRange)
        })
      }

      if (settings.excludedStoreIds && settings.excludedStoreIds.length > 0) {
        const storesResp = await api.merchantStores({ limit: settings.excludedStoreIds.length, offset: 0 } as PaginationDTO, { ids: settings.excludedStoreIds })
        settings._excludedStores = storesResp.items
      } else {
        settings._excludedStores = []
      }
    }

    const mergeGroups = async (groups: TrafficGroupDTO[], trader: TraderDTO) => {
      const visibleIds: number[] = []

      for (const group of groups) {
        if (!canShowGroupForTeamLead(group.id)) {
          continue
        }

        visibleIds.push(group.id)

        const existing = state.rewardSettingsByGroupId[group.id]
        if (existing) {
          existing._trafficGroup = group
          continue
        }

        let settings = traderSettingsByGroupId[group.id] ?? null
        if (settings === null) {
          settings = {
            trafficGroupId: group.id,
            traderId: trader.id,
            priority: TrafficPriority.LOW,
            enabled: false,
            rewardFee: '',
            teamLeadRewardFees: ['', '', '', '', ''],
            excludedStoreIds: [],
            _excludedStores: [],
            traderFeeByDealAmount: null,
            _trafficGroup: null,
            _rewardFeePercent: null,
            _teamLeadRewardFeePercents: [null, null, null, null, null],
            _groupMinRewardPercent: null,
            _groupMaxRewardPercent: null,
            _teamLeadRewardFeePercent: null,
          }
        }

        await hydrateSettings(group, settings)
        state.rewardSettingsByGroupId[group.id] = settings
      }

      return visibleIds
    }

    const resetFormState = () => {
      traderSettingsByGroupId = {}
      teamLeadRewardSettingsByGroupId = {}
      state.searchQuery = ''
      state.groupsLoaded = false
      state.visibleGroupIds = []
      state.rewardSettingsByGroupId = {}
      state.expandedRanges = {}
      state.expandedStoreBlacklist = {}
      state.historyModal = false
      state.historyPage = 1
      state.historyResponse = {
        limit: 5,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<TrafficGroupTraderRewardSettingsHistoryDTO>
    }

    const loadGroups = async (query: string, requestId: number) => {
      const trader = props.trader
      if (!trader) {
        return
      }

      const generation = traderLoadGeneration
      const groups = await api.call.trafficGroups.all(query || undefined)
      if (generation !== traderLoadGeneration || props.trader?.id !== trader.id) {
        return
      }

      const visibleIds = await mergeGroups(groups, trader)
      if (generation !== traderLoadGeneration || props.trader?.id !== trader.id) {
        return
      }

      if (requestId === searchRequestId) {
        state.visibleGroupIds = visibleIds
        state.groupsLoaded = true
      }
    }

    watch(() => props.trader, async () => {
      traderLoadGeneration++
      searchRequestId++
      const generation = traderLoadGeneration
      const requestId = searchRequestId

      if (!props.trader) {
        resetFormState()
        return;
      }

      const trader = props.trader
      resetFormState()
      modalLoader.value?.show()
      try {
        await store.dispatch("currencyMethods/loadList")
        if (trader.mainTeamLead) {
          for (const item of await api.call.teamLeadRewardSettings.byTrader(trader.id)) {
            teamLeadRewardSettingsByGroupId[item.trafficGroupId] = item
          }
        }

        for (const item of await api.call.traderRewardSettings.get(trader.id)) {
          if (canShowGroupForTeamLead(item.trafficGroupId)) {
            traderSettingsByGroupId[item.trafficGroupId] = item
          }
        }

        if (generation !== traderLoadGeneration || props.trader?.id !== trader.id) {
          return
        }

        await loadGroups('', requestId)
      } finally {
        if (generation === traderLoadGeneration && requestId === searchRequestId) {
          modalLoader.value?.hide()
        }
      }
    })

    const searchGroups = debounce(async () => {
      if (!props.trader) {
        return
      }

      const generation = traderLoadGeneration
      const requestId = ++searchRequestId
      modalLoader.value?.show()
      try {
        await loadGroups(state.searchQuery.trim(), requestId)
      } finally {
        if (generation === traderLoadGeneration && requestId === searchRequestId) {
          modalLoader.value?.hide()
        }
      }
    }, 300)

    const sortedSettings = computed<TraderGroupRewardSettingsDTO[]>(() => {
      let items = state.visibleGroupIds
        .map((id) => state.rewardSettingsByGroupId[id])
        .filter((item) => item)

      items.sort((a: TraderGroupRewardSettingsDTO, b: TraderGroupRewardSettingsDTO) => {
        return a._trafficGroup!.type > b._trafficGroup!.type ? 1 : -1;
      })

      return items as TraderGroupRewardSettingsDTO[];
    })

    const getTotalRewardValue = (item: TraderGroupRewardSettingsDTO): number => {
      return sumFloats(
        item._rewardFeePercent,
        ...item._teamLeadRewardFeePercents
      )
    }

    const sumFloats = (...args: (null | number)[]) => {
      let sum = 0;
      if (args && args.length) {
        for (const value of args) {
          sum += parseInt((value ? value * 10000 : 0).toString())
        }
      }
      return sum / 10000
    }

    const isValidRewards = (item: TraderGroupRewardSettingsDTO) => {
      if (item._trafficGroup?.type === Direction.PAY_OUT && (item._rewardFeePercent ?? 0) < 0) {
        return true
      }

      let minValue = null;
      if (item._groupMinRewardPercent) {
        minValue = (item._groupMinRewardPercent ?? 0);
      }

      if (item._teamLeadRewardFeePercent) {
        if (minValue === null) {
          minValue = (item._teamLeadRewardFeePercent ?? 0);
        } else {
          minValue = Math.min(minValue, (item._teamLeadRewardFeePercent ?? 0));
        }
      }

      if (minValue === null) {
        return false;
      }

      return getTotalRewardValue(item) <= minValue
    }

    const togglePriority = (item: TraderGroupRewardSettingsDTO) => {
      const priorities = ['low', 'medium', 'high'];
      const index = item.priority ? priorities.indexOf(item.priority) : 0;
      let newValue = priorities[index + 1];

      item.priority = (newValue ?? priorities[0]) as TrafficPriority
    }

    const priorityClass = (priority: null|string): any => {
      switch (priority) {
        case 'low':
          return {'btn-outline-secondary': true};
        case 'medium':
          return {'btn-outline-warning': true};
        case 'high':
          return {'btn-outline-danger': true};
        case 'unlimited':
          return {'btn-outline-dark': true};
      }

      return {}
    }

    const toggleRanges = (trafficGroupId: number) => {
      state.expandedRanges[trafficGroupId] = !state.expandedRanges[trafficGroupId]
    }

    const toggleStoreBlacklist = (trafficGroupId: number) => {
      state.expandedStoreBlacklist[trafficGroupId] = !state.expandedStoreBlacklist[trafficGroupId]
    }

    const addNewRange = (trafficGroupId: number) => {
      const settings = state.rewardSettingsByGroupId[trafficGroupId]
      if (!settings) return

      if (!settings.traderFeeByDealAmount) {
        settings.traderFeeByDealAmount = []
      }

      const newRange = reactive({
        minSum: '',
        maxSum: '',
        fee: '',
        currency: ''
      })
      
      settings.traderFeeByDealAmount.push(newRange)
    }

    const removeRange = (trafficGroupId: number, rangeIndex: number) => {
      const settings = state.rewardSettingsByGroupId[trafficGroupId]
      if (!settings || !settings.traderFeeByDealAmount) return

      settings.traderFeeByDealAmount.splice(rangeIndex, 1)
    }

    const validateRange = (range: TraderFeeByDealAmountDTO): string[] => {
      const errors: string[] = []
      
      const minSum = range.minSum ? (typeof range.minSum === 'string' ? parseFloat(range.minSum) : range.minSum) : null
      const maxSum = range.maxSum ? (typeof range.maxSum === 'string' ? parseFloat(range.maxSum) : range.maxSum) : null
      
      if (!minSum && !maxSum) {
        errors.push(helper.trans('validation.at_least_one_sum_required'))
      }
      
      if (minSum && maxSum && minSum >= maxSum) {
        errors.push(helper.trans('validation.min_sum_must_be_less_than_max'))
      }

      if (!range.currency || range.currency.trim() === '') {
        errors.push(helper.trans('validation.currency_required'))
      }

      if (range.currency && (range.currency.length < 3 || range.currency.length > 10)) {
        errors.push(helper.trans('validation.currency_length_invalid'))
      }
      
      return errors
    }

    const isRangeValid = (range: TraderFeeByDealAmountDTO): boolean => {
      return validateRange(range).length === 0
    }

    const updateRangeField = (trafficGroupId: number, rangeIndex: number, field: string, value: string) => {
      const settings = state.rewardSettingsByGroupId[trafficGroupId]
      if (!settings || !settings.traderFeeByDealAmount) return

      const range = settings.traderFeeByDealAmount[rangeIndex]
      if (range) {
        (range as any)[field] = value
        settings.traderFeeByDealAmount = [...settings.traderFeeByDealAmount]
        if (field === '_feePercent') {
          range.fee = helper.percentToFloat(value);
        }
      }
    }

    const availableCurrencies = computed(() => store.getters['currencyMethods/currencies'])

    return {
      ...toRefs(state),
      isOperator: computed(() => store.getters.isOperator),
      isAdmin: computed(() => store.getters.isAdmin),
      isTeamLead: computed(() => store.getters.isTeamLead),
      user: computed(() => store.getters.getUser),
      modalLoader,
      historyLoader,
      store,
      helper,
      numberFormatter,
      dateHelper,
      save,
      showHistory,
      loadHistory,
      formatHistoryValue,
      Direction,
      searchGroups,
      sortedSettings,
      isValidRewards,
      getTotalRewardValue,
      priorityClass,
      togglePriority,
      toggleRanges,
      toggleStoreBlacklist,
      addNewRange,
      removeRange,
      validateRange,
      isRangeValid,
      updateRangeField,
      availableCurrencies
    }
  }
})
</script>
